import React, { useState, useEffect, useRef } from 'react';
import config from '../config';

  const { apiBaseURL } = config;

  // Helper function to construct API URLs
  export const getApiUrl = (endpoint) => {
    return `${apiBaseURL}${endpoint}`;
  };

  const SearchBar = ({ onSearch, initialSymbol }) => {
  const [symbol, setSymbol] = useState(initialSymbol || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  
  // Cache for storing previously fetched results to minimize API calls
  const [suggestionCache, setSuggestionCache] = useState({});

  // Add useEffect to update symbol when initialSymbol changes
  useEffect(() => {
    if (initialSymbol) {
      setSymbol(initialSymbol);
    }
  }, [initialSymbol]);

  // Fetch suggestions from the API
  const fetchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    // Check if we have cached results for this query
    if (suggestionCache[query]) {
      setSuggestions(suggestionCache[query]);
      return;
    }
    
    setIsLoading(true);
    try {
      // Endpoint to get stock symbols and names that match the query
      const response = await fetch(`${apiBaseURL}/api/search?q=${query}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }
      
      const data = await response.json();
      
      // Process the results - this structure may need to be adjusted based on actual API response
      const results = data.results || [];
      
      // Update suggestions and cache
      setSuggestions(results);
      setSuggestionCache(prev => ({
        ...prev,
        [query]: results
      }));
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounce function to prevent excessive API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  // Create debounced version of fetchSuggestions
  const debouncedFetchSuggestions = useRef(
    debounce(fetchSuggestions, 300)
  ).current;

  // Effect to fetch suggestions when input changes
  useEffect(() => {
    debouncedFetchSuggestions(symbol);
  }, [symbol, debouncedFetchSuggestions]);

  // Handle clicks outside of the search component to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (symbol.trim()) {
      const trimmedSymbol = symbol.trim().toUpperCase();
      
      // Start the normal search process
      onSearch(trimmedSymbol);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const selectedSymbol = suggestion.symbol || suggestion;
    setSymbol(selectedSymbol);
    onSearch(selectedSymbol);
    setShowSuggestions(false);
  };

  // If API doesn't support search, we can use a fallback with common stock symbols
  const fallbackSuggestions = [
    { symbol: 'AAPL', name: 'Apple Inc.' },
    { symbol: 'MSFT', name: 'Microsoft Corporation' },
    { symbol: 'GOOGL', name: 'Alphabet Inc.' },
    { symbol: 'AMZN', name: 'Amazon.com, Inc.' },
    { symbol: 'META', name: 'Meta Platforms, Inc.' },
    { symbol: 'TSLA', name: 'Tesla, Inc.' },
    { symbol: 'NVDA', name: 'NVIDIA Corporation' },
    { symbol: 'JPM', name: 'JPMorgan Chase & Co.' },
    { symbol: 'V', name: 'Visa Inc.' },
    { symbol: 'JNJ', name: 'Johnson & Johnson' },
    // Add more common stocks as needed
  ];

  // Filter fallback suggestions based on input if API search fails
  const getFilteredSuggestions = () => {
    if (suggestions.length > 0) {
      return suggestions;
    }

    if (!symbol || symbol.length < 2) {
      return [];
    }

    const query = symbol.toLowerCase();
    return fallbackSuggestions.filter(
      (stock) =>
        stock.symbol.toLowerCase().includes(query) ||
        stock.name.toLowerCase().includes(query)
    ).slice(0, 8); // Limit to 8 suggestions
  };

  const filteredSuggestions = getFilteredSuggestions();

  return (
    <div className="search-bar" ref={searchRef}>
      <form onSubmit={handleSubmit} style={{width: "100%"}}>
        <div className="search-input-container">
          <input
            type="text"
            placeholder="Enter stock symbol (e.g., AAPL)"
            value={symbol}
            onChange={(e) => {
              setSymbol(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => setShowSuggestions(true)}
          />
          <button type="submit">Search</button>
        </div>
        
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="search-suggestions">
            {isLoading && <div className="suggestion-loading">Loading...</div>}
            
            {!isLoading && filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <span className="suggestion-symbol">{suggestion.symbol}</span>
                {suggestion.name && (
                  <span className="suggestion-name">{suggestion.name}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;